import React, { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSavedWords, savedWordFragmentId } from "../../hooks/useSavedWords";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { lookupSavedWordDetails } from "../../utils/savedWordLookup";
import styles from "./savedWords.module.scss";

type Selection = { text: string; reading?: string; meaning?: string; romaji?: string; rect: DOMRect; trigger: HTMLElement };
const Context = createContext<((text: string, reading: string | undefined, target: HTMLElement) => void) | null>(null);
export const useSavedWordPopover = () => useContext(Context);

export function SavedWordsProvider({ children }: { children: ReactNode }) {
    const { t } = useTranslation("common");
    const lp = useLocalizedPath();
    const { status } = useAuth(); const { save, remove, isSaved } = useSavedWords(); const [selected, setSelected] = useState<Selection | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const popover = useRef<HTMLDivElement>(null);
    const closeButton = useRef<HTMLButtonElement>(null);
    const dismiss = useCallback(() => {
        selected?.trigger.focus();
        setSelected(null);
    }, [selected]);
    useEffect(() => {
        if (!selected) return;
        const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") dismiss(); };
        const outside = (event: PointerEvent) => { if (popover.current && !popover.current.contains(event.target as Node)) dismiss(); };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("pointerdown", outside);
        closeButton.current?.focus();
        return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("pointerdown", outside); };
    }, [selected, dismiss]);
    useLayoutEffect(() => {
        if (!selected || !popover.current) return;
        const positionPopover = () => {
            const anchor = selected.trigger.getBoundingClientRect();
            const { height, width } = popover.current!.getBoundingClientRect();
            const gap = 8;
            const viewportPadding = 12;
            const below = anchor.bottom + gap;
            const top = below + height <= window.innerHeight - viewportPadding
                ? below
                : Math.max(viewportPadding, anchor.top - gap - height);
            const left = Math.max(viewportPadding, Math.min(anchor.left, window.innerWidth - width - viewportPadding));
            setPosition({ top, left });
        };
        positionPopover();
        window.addEventListener("resize", positionPopover);
        window.addEventListener("scroll", positionPopover, true);
        return () => { window.removeEventListener("resize", positionPopover); window.removeEventListener("scroll", positionPopover, true); };
    }, [selected]);
    const alreadySaved = selected && isSaved(selected.text, selected.reading);
    return <Context.Provider value={(text, reading, target) => {
        const selection = { text, reading, rect: target.getBoundingClientRect(), trigger: target };
        setPosition(null);
        setSelected(selection);
        void lookupSavedWordDetails(text, reading).then((details) => setSelected((current) => current?.trigger === target ? { ...current, ...details } : current));
    }}>
        {children}
        {selected && <div ref={popover} tabIndex={-1} className={styles.popover} role="dialog" aria-modal="false" aria-label={t("savedWordPopover.dialogLabel")} style={{ top: position?.top ?? selected.rect.bottom + 8, left: position?.left ?? selected.rect.left, visibility: position ? "visible" : "hidden" }}>
            <button ref={closeButton} className={styles.close} onClick={dismiss} aria-label={t("savedWordPopover.close")}>×</button>
            <strong lang="ja">{selected.text}</strong>{selected.reading && <span>{selected.reading}</span>}{selected.romaji && <span>{selected.romaji}</span>}
            {selected.meaning && <p>{selected.meaning}</p>}
            {status !== "authenticated" ? <p>{t("savedWordPopover.signInToSave")}</p> : alreadySaved ? <><button disabled>{t("savedWordPopover.alreadySaved")}</button><Link className={styles.view} to={lp(`/saved-words#${savedWordFragmentId(selected.text, selected.reading)}`)} onClick={dismiss}>{t("savedWordPopover.view")}</Link><button className={styles.remove} onClick={async () => { if (await remove({ text: selected.text, reading: selected.reading })) dismiss(); }}>{t("savedWordPopover.remove")}</button></> : <button onClick={async () => { if (await save({ text: selected.text, reading: selected.reading, romaji: selected.romaji, meaning: selected.meaning })) dismiss(); }}>{t("savedWordPopover.add")}</button>}
        </div>}
    </Context.Provider>;
}
